import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Play, MapPin, Phone, Mail, Star, ChevronRight, Instagram, ExternalLink } from 'lucide-react';
import { useWhatsApp } from '../hooks/useWhatsApp';

const TikTokLanding = () => {
  const [currentProject, setCurrentProject] = useState(0);
  const { openChat } = useWhatsApp();

  const projects = [
    {
      title: 'Restaurant Transformation',
      category: 'F&B Renovation',
      image: 'https://images.unsplash.com/photo-1517248135467-4c7edcad34c4?w=800',
      tags: ['#restaurantrenovation', '#sgfood', '#interiordesign']
    },
    {
      title: 'Boutique Shop Makeover',
      category: 'Retail Fit-Out',
      image: 'https://images.unsplash.com/photo-1441986300917-64674bd600d8?w=800',
      tags: ['#shopfitout', '#retaildesign', '#singapore']
    },
    {
      title: 'Modern Office Design',
      category: 'Commercial',
      image: 'https://images.unsplash.com/photo-1497366216548-37526070297c?w=800',
      tags: ['#officerenovation', '#workspace', '#moderndesign']
    }
  ];

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentProject((prev) => (prev + 1) % projects.length);
    }, 3000);
    return () => clearInterval(timer);
  }, [projects.length]);

  return (
    <div className="min-h-screen bg-black text-white">
      {/* TikTok-style Header */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-gradient-to-b from-black/80 to-transparent p-4">
        <div className="flex items-center justify-between max-w-lg mx-auto">
          <span className="text-sm font-medium">@yusendesign</span>
          <Link 
            to="/"
            className="bg-white/20 backdrop-blur px-4 py-1.5 rounded-full text-sm font-medium hover:bg-white/30 transition-colors"
          >
            Visit Website
          </Link>
        </div>
      </header>

      {/* Hero Section - Full Screen Video Style */}
      <section className="h-screen relative flex items-center justify-center overflow-hidden">
        {/* Background Image Carousel */}
        <div className="absolute inset-0">
          {projects.map((project, index) => (
            <div
              key={project.title}
              className={`absolute inset-0 transition-opacity duration-1000 ${
                index === currentProject ? 'opacity-100' : 'opacity-0'
              }`}
            >
              <img
                src={project.image}
                alt={project.title}
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black via-black/50 to-transparent" />
            </div>
          ))}
        </div>

        {/* Content Overlay */}
        <div className="relative z-10 text-center px-6 max-w-lg mx-auto">
          {/* Profile */}
          <div className="mb-8">
            <div className="w-24 h-24 mx-auto mb-4 rounded-full bg-gradient-to-r from-pink-500 via-red-500 to-yellow-500 p-1">
              <div className="w-full h-full rounded-full bg-[#1E3A5F] flex items-center justify-center">
                <span className="text-2xl font-bold">YD</span>
              </div>
            </div>
            <h1 className="text-2xl font-bold mb-1">Yusen Design & Contract</h1>
            <p className="text-gray-400">Interior Design & Renovation Company 🇸🇬</p>
          </div>

          {/* Current Project Info */}
          <div className="mb-8">
            <span className="inline-block bg-white/20 backdrop-blur px-3 py-1 rounded-full text-sm mb-2">
              {projects[currentProject].category}
            </span>
            <h2 className="text-xl font-semibold mb-2">{projects[currentProject].title}</h2>
            <div className="flex justify-center gap-2 flex-wrap">
              {projects[currentProject].tags.map((tag) => (
                <span key={tag} className="text-blue-400 text-sm">{tag}</span>
              ))}
            </div>
          </div>

          {/* Stats */}
          <div className="flex justify-center gap-8 mb-8">
            <div className="text-center">
              <p className="text-2xl font-bold">150+</p>
              <p className="text-xs text-gray-400">Projects</p>
            </div>
            <div className="text-center">
              <p className="text-2xl font-bold">38+</p>
              <p className="text-xs text-gray-400">Years</p>
            </div>
            <div className="text-center">
              <p className="text-2xl font-bold">200+</p>
              <p className="text-xs text-gray-400">Clients</p>
            </div>
          </div>

          {/* CTA Buttons */}
          <div className="space-y-3">
            <button
              onClick={() => openChat({ page: 'tiktok', context: 'hero-cta' })}
              className="block w-full bg-green-500 hover:bg-green-600 text-white font-semibold py-4 rounded-xl transition-colors"
            >
              WhatsApp Us
            </button>
            <Link
              to="/#portfolio"
              className="block w-full bg-white text-black font-semibold py-4 rounded-xl hover:bg-gray-100 transition-colors"
            >
              View Our Projects
            </Link>
          </div>

          {/* Project Indicators */}
          <div className="flex justify-center gap-2 mt-6">
            {projects.map((_, index) => (
              <button
                key={`indicator-${index}`}
                onClick={() => setCurrentProject(index)}
                className={`w-2 h-2 rounded-full transition-all ${
                  index === currentProject ? 'w-6 bg-white' : 'bg-white/40'
                }`}
              />
            ))}
          </div>
        </div>

        {/* Scroll Indicator */}
        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 animate-bounce">
          <ChevronRight className="w-6 h-6 rotate-90" />
        </div>
      </section>

      {/* Services Section - TikTok Card Style */}
      <section className="py-16 px-4 bg-gradient-to-b from-black to-gray-900">
        <div className="max-w-lg mx-auto">
          <h2 className="text-2xl font-bold text-center mb-8">Our Services</h2>
          
          <div className="space-y-4">
            {[
              { title: 'Retail Shop Fit-Out', emoji: '🏪', desc: 'Mall kiosks to boutiques' },
              { title: 'F&B Renovation', emoji: '🍽️', desc: 'Restaurants & cafes' },
              { title: 'Commercial Design', emoji: '🏢', desc: 'Offices & showrooms' },
              { title: 'Custom Carpentry', emoji: '🪚', desc: 'Feature walls & cabinets' },
              { title: 'Industrial Fit-Out', emoji: '🏭', desc: 'Factories & warehouses' }
            ].map((service) => (
              <div
                key={service.title}
                className="bg-white/5 backdrop-blur rounded-2xl p-4 flex items-center gap-4 hover:bg-white/10 transition-colors"
              >
                <span className="text-3xl">{service.emoji}</span>
                <div className="flex-1">
                  <h3 className="font-semibold">{service.title}</h3>
                  <p className="text-sm text-gray-400">{service.desc}</p>
                </div>
                <ChevronRight className="w-5 h-5 text-gray-500" />
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Hashtags Section */}
      <section className="py-12 px-4 bg-gray-900">
        <div className="max-w-lg mx-auto">
          <h2 className="text-lg font-semibold text-center mb-6">Trending Tags</h2>
          <div className="flex flex-wrap justify-center gap-2">
            {[
              '#interiordesignsingapore',
              '#renovationsg',
              '#sginterior',
              '#shopfitout',
              '#beforeandafter',
              '#renovationtransformation',
              '#singaporerenovation',
              '#retaildesign',
              '#commercialrenovation',
              '#customcarpentry'
            ].map((tag) => (
              <span
                key={tag}
                className="bg-white/10 px-3 py-1.5 rounded-full text-sm hover:bg-white/20 transition-colors cursor-pointer"
              >
                {tag}
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section className="py-16 px-4 bg-black">
        <div className="max-w-lg mx-auto text-center">
          <h2 className="text-2xl font-bold mb-6">Get In Touch</h2>
          
          <div className="space-y-4 mb-8">
            <a
              href="tel:+6567477740"
              className="flex items-center gap-3 bg-white/5 rounded-xl p-4 hover:bg-white/10 transition-colors"
            >
              <Phone className="w-5 h-5 text-blue-400" />
              <span>+65 6747-7740</span>
            </a>
            <a
              href="mailto:yusendes@singnet.com.sg"
              className="flex items-center gap-3 bg-white/5 rounded-xl p-4 hover:bg-white/10 transition-colors"
            >
              <Mail className="w-5 h-5 text-blue-400" />
              <span>yusendes@singnet.com.sg</span>
            </a>
            <div className="flex items-center gap-3 bg-white/5 rounded-xl p-4">
              <MapPin className="w-5 h-5 text-blue-400" />
              <span>114 Tagore Lane, Singapore 787547</span>
            </div>
          </div>

          {/* Final CTA */}
          <div className="space-y-3">
            <button
              onClick={() => openChat({ page: 'tiktok', context: 'footer-cta', customMessage: "Hi I found you on TikTok! I'm interested in renovation services." })}
              className="block w-full bg-gradient-to-r from-green-500 to-green-600 text-white font-semibold py-4 rounded-xl"
            >
              Message on WhatsApp
            </button>
            <Link
              to="/"
              className="block w-full border border-white/20 font-semibold py-4 rounded-xl hover:bg-white/10 transition-colors"
            >
              Visit Full Website
            </Link>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-8 px-4 bg-gray-900 text-center">
        <p className="text-gray-500 text-sm mb-2">
          Interior Design and Renovation Company Singapore
        </p>
        <p className="text-gray-600 text-xs">
          © 2025 Yusen Design & Contract Pte Ltd
        </p>
      </footer>
    </div>
  );
};

export default TikTokLanding;
